import axios, { AxiosResponse } from 'axios'

import { AgentVerificationStatus } from '@percent/admin-dashboard/api/actions/admin/admin.types'
import { AgentVerification } from '@percent/admin-dashboard/api/actions/agentVerification/agentVerification.types'
import { PBPGetResponse } from '@percent/admin-dashboard/services/pbpResponses'

export const VERIFY_CONCURRENCY = 4

export const ADMIN_RATE_LIMIT_REQUESTS = 100

export const ADMIN_RATE_LIMIT_WINDOW_MS = 30_000

export const VERIFY_RATE_LIMIT_BUDGET_SHARE = 0.75

export const VERIFY_MIN_INTERVAL_MS = Math.ceil(
  ADMIN_RATE_LIMIT_WINDOW_MS / (ADMIN_RATE_LIMIT_REQUESTS * VERIFY_RATE_LIMIT_BUDGET_SHARE)
)

export const VERIFY_RATE_LIMIT_BACKOFF_MS = 5000

export const VERIFY_MAX_BACKOFF_MS = ADMIN_RATE_LIMIT_WINDOW_MS

export const VERIFY_MAX_RATE_LIMIT_RETRIES = 3

export const NOT_FOUND_OUTCOME = 'not_found'

export const CHECK_FAILED_OUTCOME = 'check_failed'

export type BulkRejectException = {
  agentVerificationId: string
  outcome: string
}

export type BulkRejectVerification = {
  rejectedCount: number
  exceptions: BulkRejectException[]
}

type FetchAgentVerification = (args: { id: string }) => Promise<AxiosResponse<PBPGetResponse<AgentVerification>>>

type VerifyBulkRejectOutcomesProps = {
  agentVerificationIds: string[]
  fetchAgentVerification: FetchAgentVerification
  onProgress?: (checked: number) => void
  minIntervalMs?: number
}

const sleep = (ms: number) =>
  ms > 0
    ? new Promise<void>(resolve => {
        setTimeout(resolve, ms)
      })
    : Promise.resolve()

const getStatus = (error: unknown) => (axios.isAxiosError(error) ? error.response?.status : undefined)

/** The wait the server itself asked for (seconds, or an HTTP date), or undefined if it named none. */
const getRetryAfterMs = (error: unknown) => {
  const header = axios.isAxiosError(error) ? error.response?.headers?.['retry-after'] : undefined

  if (typeof header !== 'string' && typeof header !== 'number') return undefined

  const seconds = Number(header)

  if (Number.isFinite(seconds)) return Math.max(seconds * 1000, 0)

  const retryAt = Date.parse(String(header))

  return Number.isNaN(retryAt) ? undefined : Math.max(retryAt - Date.now(), 0)
}

/**
 * A Retry-After is honoured exactly as stated — the server knows when its window resets, so doubling
 * it would only stall the read-back. Escalation applies solely to the blind case where the 429 names
 * no wait at all, and is capped at one full window.
 */
const getBackoffMs = (error: unknown, attempt: number) =>
  Math.min(getRetryAfterMs(error) ?? VERIFY_RATE_LIMIT_BACKOFF_MS * 2 ** attempt, VERIFY_MAX_BACKOFF_MS)

export async function verifyBulkRejectOutcomes({
  agentVerificationIds,
  fetchAgentVerification,
  onProgress,
  minIntervalMs = VERIFY_MIN_INTERVAL_MS
}: VerifyBulkRejectOutcomesProps): Promise<BulkRejectVerification> {
  const exceptionsByIndex: (BulkRejectException | undefined)[] = []
  let rejectedCount = 0
  let checked = 0
  let nextIndex = 0
  let nextStartAt = 0

  const waitForSlot = async () => {
    const now = Date.now()
    const startAt = Math.max(now, nextStartAt)

    nextStartAt = startAt + minIntervalMs

    await sleep(startAt - now)
  }

  // Retries are recursive rather than looped so each attempt awaits its own slot from the pacer.
  const readStatus = async (
    agentVerificationId: string,
    attempt = 0
  ): Promise<AxiosResponse<PBPGetResponse<AgentVerification>>> => {
    await waitForSlot()

    try {
      return await fetchAgentVerification({ id: agentVerificationId })
    } catch (error) {
      if (getStatus(error) !== 429 || attempt >= VERIFY_MAX_RATE_LIMIT_RETRIES) throw error

      // A 429 means the whole fan-out is too fast, so hold every worker back, not just this one.
      nextStartAt = Math.max(nextStartAt, Date.now() + getBackoffMs(error, attempt))

      return readStatus(agentVerificationId, attempt + 1)
    }
  }

  const checkOne = async (agentVerificationId: string, index: number) => {
    try {
      const { data } = await readStatus(agentVerificationId)

      if (data.data.status === AgentVerificationStatus.REJECTED) {
        rejectedCount += 1
      } else {
        exceptionsByIndex[index] = { agentVerificationId, outcome: data.data.status }
      }
    } catch (error) {
      exceptionsByIndex[index] = {
        agentVerificationId,
        outcome: getStatus(error) === 404 ? NOT_FOUND_OUTCOME : CHECK_FAILED_OUTCOME
      }
    }

    checked += 1
    onProgress?.(checked)
  }

  const worker = async () => {
    while (nextIndex < agentVerificationIds.length) {
      const index = nextIndex
      nextIndex += 1

      // eslint-disable-next-line no-await-in-loop -- rows are read one at a time, on purpose
      await checkOne(agentVerificationIds[index], index)
    }
  }

  await Promise.all(Array.from({ length: Math.min(VERIFY_CONCURRENCY, agentVerificationIds.length) }, () => worker()))

  return {
    rejectedCount,
    exceptions: exceptionsByIndex.filter((exception): exception is BulkRejectException => exception !== undefined)
  }
}
